import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Layers, AppWindow, CheckCircle2, CircleDot, CircleDashed } from "lucide-react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { fmtDur, fmtClock } from "../bits";
import { Sk, sentenceLines, PointLines, InsightCallout, ExpandChevron } from "./storyBits";
import { DrawerSec, DrawerChip } from "./StoryDrawer";
import { ToolIcon, brandColorFor } from "../../../components/classic/panels";
import { toolGlyphIdFor } from "../../../components/classic/toolIcons";

/* ─────────────────────────────────────────────────────────────────────────────
   Takeaway deep read (rebuilt 2026-08-30) — the same anatomy as every other
   drill on DashboardV2: measured facts as tiles + a day strip of the windows,
   the AI read as a bold lead with a Sparkles tile, the phases as a numbered
   timeline (all open — detail is the deliverable), outcomes with semantic
   state pills, and an honest footer. Nothing grey-on-grey.
   ───────────────────────────────────────────────────────────────────────────── */

const OUTCOME = (t) => ({
  done: { c: t.status.positive, label: "Done", icon: CheckCircle2 },
  progress: { c: t.isDark ? "#F0B24A" : "#D97706", label: "In progress", icon: CircleDot },
  open: { c: t.status.negative, label: "Open", icon: CircleDashed },
});

function Tile({ c, size = 36, children }) {
  return <span aria-hidden="true" style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), flexShrink: 0, display: "grid", placeItems: "center", color: c, background: `${c}1f`, border: `1px solid ${c}47` }}>{children}</span>;
}
function Label({ t, c, children, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "22px 0 10px" }}>
      <span style={{ ...t.font.statLabel, color: c || t.c.primary }}>{children}</span>
      {right ? <span style={{ marginLeft: "auto" }}>{right}</span> : null}
    </div>
  );
}
function Pill({ t, c, children }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999, background: `${c}21`, color: c, border: `1px solid ${c}61`, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}><i style={{ width: 6, height: 6, borderRadius: 99, background: c }} />{children}</span>;
}
function Tool({ t, name, size = 18, c }) {
  if (name && (toolGlyphIdFor(name) || brandColorFor(name, t))) return <span title={name} style={{ display: "inline-flex", flexShrink: 0 }}><ToolIcon name={name} size={size} /></span>;
  return <span title={name} style={{ width: size, height: size, borderRadius: 5, display: "grid", placeItems: "center", background: c, color: "#fff", fontSize: 9, fontWeight: 800, flexShrink: 0 }}>{String(name || "?").slice(0, 2)}</span>;
}
function Stat({ t, c, icon, label, value, sub }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 12, border: `1px solid ${t.c.border}`, background: t.isDark ? t.c.surfaceMuted : t.c.surface, minWidth: 0 }}>
      <Tile c={c} size={32}>{icon}</Tile>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: t.c.textSecondary }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 750, letterSpacing: "-0.3px", fontVariantNumeric: "tabular-nums", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
        {sub ? <div style={{ fontSize: 11, color: t.c.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div> : null}
      </div>
    </div>
  );
}

/** The windows on the day, as a strip: each block is a window, width = its
 *  minutes, coloured by tool; hover reads the exact time. */
function WindowsStrip({ t, tone, windows = [] }) {
  if (!windows.length) return null;
  const first = Math.min(...windows.map((w) => w.startMs));
  const last = Math.max(...windows.map((w) => w.endMs));
  const span = Math.max(1, last - first);
  const colorFor = () => tone;
  const ticks = 4;
  return (
    <div>
      <div style={{ position: "relative", height: 30, borderRadius: 9, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, boxShadow: `inset 0 0 0 1px ${t.c.border}`, overflow: "hidden" }}>
        {windows.map((w, i) => {
          const c = colorFor(w.app || "");
          const l = ((w.startMs - first) / span) * 100; const wd = Math.max(0.8, ((w.endMs - w.startMs) / span) * 100);
          return (
            <span key={i} title={`${fmtClock(w.startMs)} – ${fmtClock(w.endMs)} · ${fmtDur(w.minutes)}${w.app ? ` · ${w.app}` : ""}${w.title ? ` · ${w.title}` : ""}`}
              style={{ position: "absolute", top: 4, bottom: 4, left: `${l}%`, width: `${wd}%`, borderRadius: 5, background: c, opacity: 0.92 }} />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 10.5, fontWeight: 700, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
        {Array.from({ length: ticks + 1 }, (_, i) => <span key={i}>{fmtClock(first + (span * i) / ticks)}</span>)}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {[...new Set(windows.map((w) => w.app).filter(Boolean))].slice(0, 6).map((app) => (
          <span key={app} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 650, padding: "4px 10px 4px 6px", borderRadius: 999, border: `1px solid ${t.c.border}`, color: t.c.text }}>
            <Tool t={t} name={app} size={14} c={colorFor(app)} />{app}
            <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>· {fmtDur(windows.filter((w) => w.app === app).reduce((a, w) => a + w.minutes, 0))}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function TakeawayDeepRead({ tk, userId, date, source }) {
  const t = useDashTheme();
  const tone = t.c.primary;
  const [facts, setFacts] = React.useState(null);
  const [headline, setHeadline] = React.useState("");
  const [phases, setPhases] = React.useState([]);
  const [outcomes, setOutcomes] = React.useState([]);
  const [running, setRunning] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(() => new Set()); // phases are open by default
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/takeaway-detail`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            userId, date, source, kind: tk.kind, title: tk.title, detail: tk.detail,
            threadKeys: tk.threadKeys || [],
            blockStarts: (tk.demonstration || []).map((d) => d.startMs),
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not open this takeaway.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "facts") setFacts(d.facts);
            else if (d.type === "headline") setHeadline(d.text);
            else if (d.type === "phase") setPhases((c) => [...c, d]);
            else if (d.type === "outcome") setOutcomes((c) => [...c, d]);
            else if (d.type === "error") setError(d.message);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally {
        setRunning(false);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const OUT = OUTCOME(t);
  const green = t.status.positive;
  const isObservation = tk.kind === "risk" || tk.kind === "pattern";
  const windows = facts?.windowsList?.length ? facts.windowsList : (tk.demonstration || []).map((d) => ({ startMs: d.startMs, endMs: d.endMs, minutes: Math.max(1, Math.round((d.endMs - d.startMs) / 60000)), app: d.app || null }));
  const doneN = outcomes.filter((o) => o.state === "done").length;
  const openN = outcomes.filter((o) => o.state === "open").length;

  return (
    <div>
      {/* ── measured facts ─────────────────────────────────────────────── */}
      {facts ? (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
            <Stat t={t} c={tone} icon={<Clock size={15} />} label="Time on it" value={fmtDur(facts.totalMin || 0)} sub={facts.firstMs ? `${fmtClock(facts.firstMs)} – ${fmtClock(facts.lastMs)}` : null} />
            <Stat t={t} c={t.c.primary} icon={<Layers size={15} />} label="Windows" value={`${facts.windows || 0}`} sub={facts.longestMin ? `longest ${fmtDur(facts.longestMin)}${facts.longestAt ? ` at ${fmtClock(facts.longestAt)}` : ""}` : null} />
            <Stat t={t} c={t.c.primary} icon={<AppWindow size={15} />} label="Tools" value={(facts.apps || []).length ? facts.apps.slice(0, 2).join(" · ") + ((facts.apps || []).length > 2 ? ` +${facts.apps.length - 2}` : "") : "—"} sub={facts.switches ? `${facts.switches} switch${facts.switches === 1 ? "" : "es"}` : "no switching"} />
          </div>
          {windows.length ? (
            <>
              <Label t={t} c={tone}>When it ran</Label>
              <WindowsStrip t={t} tone={tone} windows={windows} />
            </>
          ) : null}
          {facts.artifacts?.length ? (
            <>
              <Label t={t} c={tone}>What it touched</Label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {facts.artifacts.slice(0, 10).map((a) => <DrawerChip key={a} style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", borderRadius: 7 }}>{a}</DrawerChip>)}
              </div>
            </>
          ) : null}
        </motion.div>
      ) : (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>{[0, 1, 2].map((i) => <Sk key={i} h={56} r={12} />)}</div>
          <div style={{ marginTop: 18 }}><Sk h={9} w={110} r={5} /><Sk h={30} r={9} style={{ marginTop: 10 }} /></div>
        </div>
      )}

      {/* ── the AI read ────────────────────────────────────────────────── */}
      <DrawerSec title={isObservation ? "What this shows" : "What this actually was"}>
        {headline ? (
          <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.65, maxWidth: "68ch", color: t.c.text }}>{headline}</div>
        ) : tk.detail && !running ? (
          <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.6, color: t.c.textSecondary, maxWidth: "68ch" }}>{tk.detail}</div>
        ) : (
          <div><Sk h={16} w="88%" /><Sk h={16} w="62%" style={{ marginTop: 7 }} /></div>
        )}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
          <DrawerChip>{running ? "Writing…" : "Written from the work"}</DrawerChip>
          {facts?.entries ? <DrawerChip>{Number(facts.entries).toLocaleString()} entries</DrawerChip> : null}
          {phases.length ? <DrawerChip>{phases.length} phase{phases.length === 1 ? "" : "s"}</DrawerChip> : null}
          {outcomes.length ? <DrawerChip>{doneN} done{openN ? ` · ${openN} open` : ""}</DrawerChip> : null}
        </div>
      </DrawerSec>

      {/* ── how it unfolded ────────────────────────────────────────────── */}
      <Label t={t} c={tone} right={phases.length > 1 ? <button type="button" className="st-reset st-focusable" onClick={() => setCollapsed((c) => (c.size ? new Set() : new Set(phases.map((_, i) => i))))} style={{ fontSize: 11.5, fontWeight: 700, color: tone, cursor: "pointer", background: "none", border: 0 }}>{collapsed.size ? "Expand all" : "Collapse all"}</button> : null}>
        {isObservation ? "Where it showed" : "How it unfolded"}
      </Label>
      <div style={{ position: "relative" }}>
        {phases.map((p, i) => {
          const lines = sentenceLines(p.what);
          const lead = p.lead || lines[0] || "";
          const points = p.points?.length ? p.points : lines.slice(1);
          const isOpen = !collapsed.has(i);
          const last = i === phases.length - 1;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 12 }}>
              {/* rail: time + numbered ink tile + connector */}
              <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 2 }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: t.c.text, fontVariantNumeric: "tabular-nums", marginBottom: 6 }}>{p.time || "—"}</span>
                <span style={{ width: 26, height: 26, borderRadius: 8, display: "grid", placeItems: "center", background: t.c.primarySoft, color: t.c.primary, fontSize: 11.5, fontWeight: 700, zIndex: 1 }}>{i + 1}</span>
                {!last ? <span aria-hidden="true" style={{ flex: 1, width: 2, minHeight: 18, background: t.c.border, margin: "6px 0 0" }} /> : null}
              </div>
              <div style={{ paddingBottom: last ? 0 : 18, minWidth: 0 }}>
                <button type="button" className="st-reset st-focusable" onClick={() => setCollapsed((c) => { const n = new Set(c); if (n.has(i)) n.delete(i); else n.add(i); return n; })} aria-expanded={isOpen}
                  style={{ display: "flex", gap: 10, alignItems: "flex-start", width: "100%", textAlign: "left", cursor: "pointer", background: "none", border: 0, padding: "4px 0" }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px", lineHeight: 1.45, color: t.c.text }}>{lead}</span>
                  <ExpandChevron open={isOpen} tone={tone} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
                      <div style={{ paddingTop: 6 }}>
                        {points.length > 0 ? <PointLines points={points} tone={tone} gap={8} /> : null}
                        <InsightCallout text={p.insight} tone={tone} />
                        {p.items?.length > 0 && (
                          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                            {p.items.map((x) => <DrawerChip key={x} style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", borderRadius: 6 }}>{x}</DrawerChip>)}
                          </span>
                        )}
                        {p.evidence && (
                          <span style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 12, color: t.c.textSecondary, marginTop: 9 }}>
                            <i style={{ width: 6, height: 6, borderRadius: 99, background: green, marginTop: 6, flexShrink: 0 }} /><span><b style={{ color: t.c.text, fontWeight: 650 }}>Shown by</b> · {p.evidence}</span>
                          </span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
        {running && (
          <div style={{ display: "grid", gridTemplateColumns: "60px 1fr", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 2 }}><Sk w={34} h={11} /><Sk w={26} h={26} r={8} style={{ marginTop: 6 }} /></div>
            <div><Sk w="86%" h={14} /><Sk w="70%" h={12} style={{ marginTop: 9 }} /><Sk w="58%" h={12} style={{ marginTop: 7 }} /></div>
          </div>
        )}
        {!running && !phases.length && !error && (
          <div style={{ fontSize: 13, color: t.c.textSecondary }}>Nothing more to add — the read above is the whole account.</div>
        )}
      </div>

      {/* ── where it landed ────────────────────────────────────────────── */}
      {outcomes.length > 0 && (
        <>
          <Label t={t} c={tone} right={<><Pill t={t} c={green}>{doneN} done</Pill>{outcomes.some((o) => o.state === "progress") ? <span style={{ marginLeft: 6 }}><Pill t={t} c={OUT.progress.c}>{outcomes.filter((o) => o.state === "progress").length} in progress</Pill></span> : null}{openN ? <span style={{ marginLeft: 6 }}><Pill t={t} c={t.status.negative}>{openN} open</Pill></span> : null}</>}>Where it landed</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {outcomes.map((o, i) => {
              const m = OUT[o.state] || OUT.done; const Icon = m.icon;
              return (
                <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "10px 12px", borderRadius: 12, border: `1px solid ${m.c}44`, background: `${m.c}0f` }}>
                  <Tile c={m.c} size={28}><Icon size={14} /></Tile>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><Pill t={t} c={m.c}>{m.label}</Pill></span>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: t.c.text, lineHeight: 1.5, marginTop: 6 }}>{o.text}</span>
                    {o.evidence && <span style={{ display: "block", fontSize: 12, color: t.c.textSecondary, marginTop: 3 }}><b style={{ color: t.c.text, fontWeight: 650 }}>Shown by</b> · {o.evidence}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* demonstration stretches for observations — measured, always true */}
      {isObservation && tk.demonstration?.length > 0 && !facts?.windowsList?.length && (
        <>
          <Label t={t} c={tone}>The stretches that show it</Label>
          {tk.demonstration.slice(0, 6).map((d, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "8px 0", alignItems: "center", borderTop: i ? `1px solid ${t.c.border}` : "none" }}>
              <span style={{ flex: "none", width: 118, fontSize: 12.5, fontWeight: 700, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtClock(d.startMs)} – {fmtClock(d.endMs)}</span>
              <Tool t={t} name={d.app} size={16} c={tone} />
              <span style={{ fontSize: 13, color: t.c.textSecondary }}>{d.app}{d.metric ? ` · ${d.metric}` : ""}</span>
            </div>
          ))}
        </>
      )}

      {error ? (
        <div style={{ marginTop: 18, fontSize: 13, color: t.status.negative, background: t.status.negativeSoft, padding: "10px 13px", borderRadius: 10 }}>{error}</div>
      ) : (
        <div style={{ marginTop: 22, paddingTop: 12, borderTop: `1px solid ${t.c.border}`, display: "flex", gap: 8, alignItems: "center", fontSize: 11.5, color: t.c.textSecondary }}>
          <i style={{ width: 7, height: 7, borderRadius: 99, background: green, flexShrink: 0 }} />
          {running ? "Facts first — the written account lands as the model finishes each phase." : "Times, windows and tools are measured; every written line carries the thing that shows it. Nothing is invented."}
        </div>
      )}
    </div>
  );
}

export default TakeawayDeepRead;
